(function (scope) {
  'use strict';
  const stages = ['recipient', 'feeling', 'gift-level', 'result'];
  const fields = ['recipient', 'feeling', 'format'];
  const instances = scope.__lynkGiftFinderInstances || new WeakMap();
  scope.__lynkGiftFinderInstances = instances;
  let serial = scope.__lynkGiftFinderSerial || 0;
  const owns = (object, key) => typeof key === 'string' && Object.hasOwn(object || {}, key);
  function cleanState(data, value) {
    const state = { step: 0, recipient: null, feeling: null, format: null };
    if (!value || typeof value !== 'object') return state;
    if (owns(data.recipients, value.recipient)) state.recipient = value.recipient;
    if (owns(data.feelings, value.feeling) && owns(data.scents, data.feelings[value.feeling].scent)) state.feeling = value.feeling;
    if (owns(data.formats, value.format)) state.format = value.format;
    // Migrate only finder progress; saved sets retain their stable tier keys.
    else if (owns(data.giftSets, value.tier)) state.format = data.giftSets[value.tier].formats.find(key => owns(data.formats, key)) || null;
    const mapping = data.scents?.[data.feelings?.[state.feeling]?.scent]?.productMappings?.[state.format];
    if (state.feeling && !usableMapping(mapping)) state.format = null;
    const firstMissing = fields.findIndex(field => !state[field]);
    const maximum = firstMissing === -1 ? 3 : firstMissing;
    state.step = Number.isInteger(value.step) ? Math.max(0, Math.min(maximum, value.step)) : 0;
    return state;
  }
  function productUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try { const url = new URL(value, scope.document?.baseURI || 'https://lynkfragrances.com/'); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch (_) { return ''; }
  }
  function usableMapping(mapping) {
    return mapping?.configured === true && /^\d+$/.test(String(mapping.variantId || '')) && typeof mapping.price === 'string' && Boolean(mapping.price.trim()) && Boolean(productUrl(mapping.url));
  }
  function resolveResult(data, selection) {
    const state = cleanState(data, { ...selection, step: 3 });
    if (state.step !== 3) return null;
    const scentKey = data.feelings[state.feeling].scent;
    const scent = data.scents[scentKey];
    const format = data.formats[state.format];
    const mapping = scent.productMappings?.[state.format];
    if (!usableMapping(mapping)) return null;
    const template = state.recipient === 'mum'
      ? data.letterTemplates?.mum || { to: data.recipients.mum, message: data.letter }
      : data.letterTemplates?.[state.recipient] || { to: data.recipients[state.recipient], message: '' };
    return {
      gift: { kind: 'single', format: state.format, scent: scentKey, recipient: state.recipient, feeling: state.feeling },
      scent, format, mapping, recipientName: data.recipients[state.recipient],
      price: mapping.price, nativePrice: true, url: productUrl(mapping.url), available: mapping.available === true,
      contents: [{ key: state.format, ...format }],
      letter: { to: String(template.to || ''), message: String(template.message || '') }
    };
  }
  function init(root, options = {}) {
    if (!root?.querySelector) throw new TypeError('Gift finder requires its root element.');
    const existing = instances.get(root);
    if (existing) { existing.configure(options); return existing; }
    let core = options.core || null;
    let data = options.data || core?.data || scope.LYNK_CHRISTMAS_DATA;
    if (!data?.formats || !data?.scents || !data?.feelings || !data?.recipients) throw new TypeError('Gift finder requires the campaign data.');
    const controller = new AbortController(), { signal } = controller;
    const $ = selector => root.querySelector(selector);
    const $$ = selector => Array.from(root.querySelectorAll(selector));
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal });
    const media = scope.matchMedia('(prefers-reduced-motion: reduce)');
    const text = (key, fallback, values = {}) => {
      let output = typeof data.ui?.[key] === 'string' ? data.ui[key] : fallback;
      Object.entries(values).forEach(([key, value]) => { output = output.replaceAll('{' + key + '}', String(value)); });
      return output;
    };
    const storageKey = (data.storageKey || 'lynk-christmas-2026-v12') + ':gift-finder';
    let stored = root.lynkGiftFinderState;
    if (!stored) { try { stored = JSON.parse(scope.sessionStorage.getItem(storageKey)); } catch (_) {} }
    let state = cleanState(data, stored);
    let scene = options.scene || null;
    let sceneAttempt = null, sceneGeneration = 0, sceneBlocked = false;
    let suppliedScene = scene;
    scene = null;
    let pendingReady = Promise.resolve(null);
    let saving = false;
    const form = $('[data-finder-form]');
    const next = $('[data-finder-next]');
    const save = $('[data-finder-save]');
    const view = $('[data-finder-view-product]') || $('[data-finder-view-set]');
    const sceneContainer = $('[data-finder-scene]');
    const sectionNames = [text('finderStepSomeone', 'Someone'), text('finderStepFeeling', 'A feeling'), text('finderStepGift', 'The gift'), text('finderStepLove', 'With love')];
    if (!root.dataset.finderIdsScoped) {
      const prefix = 'lynk-finder-' + (++serial), idMap = new Map();
      scope.__lynkGiftFinderSerial = serial;
      $$('[id]').forEach(element => { idMap.set(element.id, prefix + '-' + element.id); });
      $$('[id]').forEach(element => { element.id = idMap.get(element.id); });
      $$('[aria-labelledby],[aria-describedby],[for]').forEach(element => {
        ['aria-labelledby', 'aria-describedby', 'for'].forEach(attribute => {
          if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => idMap.get(id) || id).join(' '));
        });
      });
      $$('input[type=radio]').forEach(input => { input.name = prefix + '-' + input.name; });
      root.dataset.finderIdsScoped = 'true';
    }
    if (options.collectionUrl) $('[data-finder-collection-link]').href = options.collectionUrl;
    if (options.posterUrl) {
      try {
        const url = new URL(options.posterUrl, document.baseURI);
        if (['https:', 'http:'].includes(url.protocol)) { $('[data-finder-still]').src = url.href; $('[data-finder-still]').hidden = false; }
      } catch (_) {}
    }
    function persistState() {
      root.lynkGiftFinderState = { ...state };
      try { scope.sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) {}
    }
    function sceneContext() {
      return { ...state, scent: data.feelings[state.feeling]?.scent || null, reducedMotion: media.matches, immediate: media.matches };
    }
    function updateScene() {
      if (!scene?.setStage) return;
      try { scene.resize?.(); scene.setStage(stages[state.step], sceneContext()); }
      catch (_) { root.dataset.sceneError = 'true'; }
    }
    function coreReady() { return typeof core?.saveGift === 'function' && typeof core?.getGifts === 'function'; }
    function renderResult() {
      const result = resolveResult(data, state);
      if (!result) return;
      $('[data-finder-result-recipient]').textContent = state.recipient === 'myself'
        ? text('finderChosenForYou', 'A little moment, chosen for you.')
        : text('finderChosenFor', 'Chosen with {recipient} in mind.', { recipient: result.recipientName });
      $('[data-finder-result-scent]').textContent = result.scent.name;
      $('[data-finder-result-notes]').textContent = result.scent.notes;
      $('[data-finder-result-reason]').textContent = result.scent.reason;
      $('[data-finder-result-tier]').textContent = result.mapping.title || result.format.name;
      $('[data-finder-result-price]').textContent = result.price;
      $('[data-finder-result-price-note]').textContent = text('finderNativePrice', 'Current product price in SGD.') + ' ' + (result.available ? text('finderInStock', 'Available') : text('soldOut', 'Sold out'));
      $('[data-finder-result-contents]').replaceChildren(...result.contents.map(format => {
        const item = document.createElement('li');
        item.textContent = format.name + ' · ' + format.size + (format.detail ? ' · ' + format.detail : '');
        return item;
      }));
      $('[data-finder-letter-to]').textContent = text('finderDear', 'Dear {recipient},', { recipient: result.letter.to });
      $('[data-finder-letter-message]').textContent = result.letter.message;
      $('[data-finder-letter-copy]').disabled = !result.letter.message;
      $('[data-finder-letter-edit]').hidden = typeof core?.openLetter !== 'function' && typeof options.onLetter !== 'function';
      save.disabled = saving || !coreReady();
      view.href = result.url; view.hidden = false;
    }
    function render({ focus = false } = {}) {
      root.dataset.finderStage = stages[state.step];
      $$('[data-finder-step]').forEach(section => { section.hidden = Number(section.dataset.finderStep) !== state.step; });
      $$('[data-finder-progress]').forEach(item => {
        const index = Number(item.dataset.finderProgress);
        if (index === state.step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
        item.classList.toggle('is-complete', index < state.step);
      });
      $$('[data-finder-choice]').forEach(input => { input.checked = state[input.dataset.finderChoice] === input.value; });
      $('[data-finder-announcement]').textContent = text('finderProgress', '{name} · Step {step} of 4', { name: sectionNames[state.step], step: state.step + 1 });
      const contextNames = { mum: text('finderMum', 'Mum'), loved: text('finderLoved', 'someone you love'), friend: text('finderFriend', 'your friend'), myself: text('finderYou', 'you') };
      $('[data-finder-recipient-context]').textContent = text('finderRecipientContext', 'A small feeling, chosen for {recipient}.', { recipient: contextNames[state.recipient] || text('finderThem', 'them') });
      const scentKey = data.feelings[state.feeling]?.scent;
      $$('[data-finder-choice="format"]').forEach(input => {
        const mapping = data.scents[scentKey]?.productMappings?.[input.value];
        const usable = usableMapping(mapping);
        input.disabled = !usable;
        const label = input.closest('label');
        const price = label?.querySelector('[data-finder-format-price]');
        const stock = label?.querySelector('[data-finder-format-stock]');
        const description = label?.querySelector('small');
        const format = data.formats[input.value];
        if (price) price.textContent = usable ? mapping.price : '';
        if (stock) stock.textContent = !usable ? text('notAvailable', 'Not yet available') : mapping.available === true ? text('finderInStock', 'Available') : text('soldOut', 'Sold out');
        if (description && format) description.textContent = format.name + ' · ' + format.size + (format.detail ? ' · ' + format.detail : '');
      });
      $('[data-finder-pricing-note]').textContent = text('finderNativeChoiceNote', 'Actual Christmas products and current prices in SGD. Sold-out products still have details.');
      $('[data-finder-back]').disabled = state.step === 0;
      next.hidden = state.step === 3;
      next.disabled = state.step === 3 || !state[fields[state.step]];
      const nextIcon = next.querySelector('.lc-icon');
      const nextLabel = state.step === 2 ? text('finderShowMatch', 'Find their gift') : text('finderContinue', 'Continue');
      next.replaceChildren(document.createTextNode(nextLabel.replace(/[\s→\uFE0F]+$/u, '').trim() + ' '));
      if (nextIcon) next.append(nextIcon);
      $('[data-finder-validation]').textContent = '';
      if (state.step === 3) renderResult();
      persistState(); updateScene();
      root.dispatchEvent(new CustomEvent('lynk:gift-finder-stage', { bubbles: true, detail: { stage: stages[state.step], ...sceneContext() } }));
      if (focus) $('[data-finder-step="' + state.step + '"] h1, [data-finder-step="' + state.step + '"] h2')?.focus();
    }
    on(form, 'change', event => {
      const input = event.target.closest('[data-finder-choice]');
      if (!input || !input.checked || fields[state.step] !== input.dataset.finderChoice) return;
      state[input.dataset.finderChoice] = input.value;
      state = cleanState(data, state);
      $('[data-finder-save-feedback]').textContent = '';
      $('[data-finder-letter-feedback]').textContent = '';
      render();
    });
    on(form, 'submit', event => {
      event.preventDefault();
      if (state.step === 3) return;
      if (!state[fields[state.step]]) { $('[data-finder-validation]').textContent = text('finderChooseFirst', 'Choose one to continue.'); return; }
      state.step++; render({ focus: true });
    });
    on($('[data-finder-back]'), 'click', () => { if (state.step > 0) { state.step--; render({ focus: true }); } });
    on($('[data-finder-reset]'), 'click', () => {
      state = cleanState(data, null); saving = false;
      $('[data-finder-save-feedback]').textContent = ''; $('[data-finder-letter-feedback]').textContent = '';
      render({ focus: true });
    });
    on(save, 'click', async () => {
      const result = resolveResult(data, state);
      if (saving || !result || !coreReady()) return;
      saving = true; save.disabled = true;
      try {
        const outcome = await core.saveGift({ ...result.gift });
        const gifts = await core.getGifts();
        if (!Array.isArray(gifts) || !gifts.some(gift => gift.kind !== 'set' && gift.format === result.gift.format && gift.scent === result.gift.scent)) throw new Error('Gift was not retained.');
        if (signal.aborted) return;
        const persisted = typeof outcome === 'boolean' ? outcome : outcome?.persisted;
        $('[data-finder-save-feedback]').textContent = persisted === true
          ? text('finderSaved', 'Saved in this browser. Find it under Saved ideas.')
          : text('finderSavedVisit', 'Kept under Saved ideas for this visit. This browser may not retain it for later.');
        root.dispatchEvent(new CustomEvent('lynk:gift-finder-saved', { bubbles: true, detail: { gift: result.gift, persisted: persisted === true } }));
      } catch (_) {
        if (!signal.aborted) $('[data-finder-save-feedback]').textContent = text('finderSaveError', 'Your idea could not be saved. Please try again.');
      } finally { saving = false; if (!signal.aborted) save.disabled = !coreReady(); }
    });

    on($('[data-finder-letter-edit]'), 'click', () => {
      const result = resolveResult(data, state); if (!result) return;
      if (typeof options.onLetter === 'function') options.onLetter(result.gift.recipient, result.letter);
      else core?.openLetter?.(result.gift.recipient);
    });
    on($('[data-finder-letter-copy]'), 'click', async () => {
      const result = resolveResult(data, state); if (!result?.letter.message) return;
      const content = text('finderDear', 'Dear {recipient},', { recipient: result.letter.to }) + '\n\n' + result.letter.message;
      try { await navigator.clipboard.writeText(content); $('[data-finder-letter-feedback]').textContent = text('finderLetterCopied', 'Copied, ready to share.'); }
      catch (_) {
        const range = document.createRange(); range.selectNodeContents($('[data-finder-letter-message]'));
        const selection = scope.getSelection(); selection.removeAllRanges(); selection.addRange(range);
        $('[data-finder-letter-feedback]').textContent = text('finderLetterFallback', 'Your words are selected. Use Copy to keep them.');
      }
    });
    function stopScene() {
      sceneGeneration++;
      sceneAttempt?.abort(); sceneAttempt = null;
      scene?.destroy?.(); scene = null;
      root.dataset.sceneReady = 'false';
    }
    function startScene() {
      stopScene();
      if (signal.aborted || media.matches || sceneBlocked) { suppliedScene?.destroy?.(); suppliedScene = null; return Promise.resolve(null); }
      const factory = options.sceneFactory || scope.createFlightScene;
      const attempt = new AbortController(), current = sceneGeneration;
      sceneAttempt = attempt;
      const provided = suppliedScene; suppliedScene = null;
      const pendingScene = provided ? Promise.resolve(provided) : typeof factory === 'function'
        ? Promise.resolve().then(() => attempt.signal.aborted ? null : factory(sceneContainer, { mode: 'gift-finder', reducedMotion: media.matches, initialStage: stages[state.step], signal: attempt.signal }))
        : Promise.resolve(null);
      pendingReady = pendingScene.then(value => {
        if (signal.aborted || attempt.signal.aborted || current !== sceneGeneration || media.matches) { value?.destroy?.(); return null; }
        scene = value;
        root.dataset.sceneReady = String(Boolean(scene?.setStage));
        updateScene();
        return scene;
      }).catch(() => {
        if (!signal.aborted && !attempt.signal.aborted && current === sceneGeneration) { root.dataset.sceneError = 'true'; root.dataset.sceneReady = 'false'; }
        return null;
      });
      return pendingReady;
    }
    on(media, 'change', () => {
      if (media.matches) { updateScene(); stopScene(); }
      else startScene();
    });
    sceneContainer?.addEventListener('webglcontextlost', event => {
      event.preventDefault(); sceneBlocked = true; root.dataset.sceneError = 'true'; stopScene();
    }, { signal, capture: true });
    on($('details'), 'toggle', updateScene);
    render();
    pendingReady = startScene();
    const api = {
      get ready() { return pendingReady; }, getState: () => ({ ...state }), getResult: () => resolveResult(data, state),
      setCore(value) { core = value; render(); },
      configure(value = {}) {
        const newScene = value.sceneFactory && value.sceneFactory !== options.sceneFactory;
        Object.assign(options, value);
        if (value.core) core = value.core;
        if (value.data) { data = value.data; state = cleanState(data, state); }
        if (value.posterUrl) { const still = $('[data-finder-still]'); if (still && productUrl(value.posterUrl)) { still.src = productUrl(value.posterUrl); still.hidden = false; } }
        render();
        if (newScene) pendingReady = startScene();
      },
      reset() { state = cleanState(data, null); render({ focus: true }); },
      destroy() { if (signal.aborted) return; controller.abort(); stopScene(); suppliedScene?.destroy?.(); suppliedScene = null; if (instances.get(root) === api) instances.delete(root); if (root.lynkGiftFinderApi === api) delete root.lynkGiftFinderApi; }
    };
    instances.set(root, api); root.lynkGiftFinderApi = api; return api;
  }
  const api = { init, cleanState, resolveResult, stages: [...stages] };
  scope.LynkGiftFinder = api;
  scope.initLynkGiftFinder = init;
  // Functional quiz boot is independent of the optional 3D experience bundle.
  function initializeIn(container) {
    const finders = [...(container.matches?.('[data-gift-finder]') ? [container] : []), ...container.querySelectorAll?.('[data-gift-finder]') || []];
    finders.forEach(finder => {
      const campaign = finder.closest('[data-lynk-christmas]');
      if (!campaign?.isConnected || scope.__lynkV13UnloadedRoots?.has(campaign)) return;
      const core = campaign.lynkChristmasApi || scope.initLynkChristmas?.(campaign);
      if (!core) return;
      init(finder, { core, data: core.data, collectionUrl: finder.dataset.collectionUrl, posterUrl: finder.dataset.posterUrl });
    });
  }
  if (scope.document) {
    const document = scope.document;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeIn(document), { once: true });
    else initializeIn(document);
    if (!scope.__lynkGiftFinderLifecycle) {
      document.addEventListener('lynk:ready', event => initializeIn(event.target));
      document.addEventListener('shopify:section:load', event => initializeIn(event.target));
      document.addEventListener('shopify:section:unload', event => {
        const roots = [...(event.target.matches?.('[data-gift-finder]') ? [event.target] : []), ...event.target.querySelectorAll?.('[data-gift-finder]') || []];
        roots.forEach(root => instances.get(root)?.destroy());
      });
      scope.__lynkGiftFinderLifecycle = true;
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(typeof window === 'undefined' ? globalThis : window));
